import { ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { Theme, Tooltip } from '@mui/material';
import useAuth from 'hooks/useAuth';
import BetaIcon from 'pages/admin/components/BetaIcon';
import { useTranslator } from 'translation/useTranslator';
import { useFeatureFlags } from 'contexts/FeatureFlagsContext';
import { AuthUser } from '../@types/authentication';
import { FeatureFlagId } from '../@types/featureFlags';
import { PermissionDeniedAlert } from '../guards/PermissionDeniedAlert';
import { AccessRule } from './rules';
import { AccessResult, getAccess } from './evaluate';

/**
 * Resolves the effective user and claims for the current session (the JWT claims).
 *
 * Exposed so that non-component access checks (e.g. useGuardedDispatch) can reuse the same claim
 * resolution that AccessGuard and useAccess rely on, keeping decisions consistent.
 */
export const useResolvedAccessContext = (): { user: AuthUser; currentClaims: string[] } => {
  const { user } = useAuth();
  const currentClaims = user?.claims ?? [];

  return { user: { ...user, claims: currentClaims } as AuthUser, currentClaims };
};

export const useAccessControl = (rule: AccessRule | undefined): AccessResult => {
  const { user, currentClaims } = useResolvedAccessContext();
  return getAccess({ user, rule, currentClaims });
};

export const useAccess = (rule: AccessRule | undefined): boolean => useAccessControl(rule).canView;

enum AccessTagPosition {
  Right = 'right',
  TopRight = 'topRight'
}

type AccessGuardProps = {
  rule?: AccessRule;
  featureFlagAccess?: FeatureFlagId;
  children: ReactNode;
  displayAccessTag?: keyof typeof AccessTagPosition;
  displayNoAccess?: boolean;
};

const AccessGuard = ({
  rule,
  featureFlagAccess,
  children,
  displayAccessTag,
  displayNoAccess
}: AccessGuardProps) => {
  const { isEnabledForCurrentOrganization } = useFeatureFlags();
  const translator = useTranslator();
  const { canView, canEdit, requiredEditRole, isBeta } = useAccessControl(rule);

  const featureEnabledForOrg = featureFlagAccess
    ? isEnabledForCurrentOrganization(featureFlagAccess)
    : true;

  if (!canView || !featureEnabledForOrg) {
    return displayNoAccess ? <PermissionDeniedAlert /> : null;
  }

  return (
    <Tooltip
      title={
        !canEdit && requiredEditRole ? translator.roleRequiredDescription(requiredEditRole) : ''
      }
      placement="top"
    >
      <Wrapper block_edit={`${!canEdit}`}>
        <div className="guarded-content">
          {children}
          {displayAccessTag && (
            <AccessTagWrapper position={AccessTagPosition[displayAccessTag]}>
              {isBeta && <BetaIcon />}
            </AccessTagWrapper>
          )}
        </div>
      </Wrapper>
    </Tooltip>
  );
};

export default AccessGuard;

const Wrapper = styled('div')(({ block_edit }: { block_edit: string; theme?: Theme }) => {
  return {
    position: 'relative',
    '&:hover': {
      cursor: block_edit === 'true' ? 'not-allowed' : 'inherit'
    },
    '> .guarded-content': {
      pointerEvents: block_edit === 'true' ? 'none' : 'inherit'
    }
  };
});

const AccessTagWrapper = styled('div')(({ position }: { position?: AccessTagPosition }) => ({
  visibility: position ? 'visible' : 'hidden',
  position: 'absolute',
  top: position === AccessTagPosition.TopRight ? 0 : '50%',
  right: 0,
  transform:
    position === AccessTagPosition.TopRight ? 'translate(50%, -50%)' : 'translate(100%, -50%)',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingLeft: position === AccessTagPosition.Right ? '.2rem' : 0
}));
